import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Chip,
  CircularProgress,
  Button,
  alpha,
  useTheme,
  Divider,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Description as DocIcon,
  TableChart as ExcelIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { assetDocumentsApi } from '../../../services/api';

const FILE_TYPE_ICONS = {
  pdf: <PdfIcon sx={{ color: '#d32f2f' }} />,
  image: <ImageIcon sx={{ color: '#1976d2' }} />,
  xlsx: <ExcelIcon sx={{ color: '#2e7d32' }} />,
  doc: <DocIcon sx={{ color: '#1565c0' }} />,
  other: <FileIcon sx={{ color: '#757575' }} />,
};

function formatFileSize(bytes) {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

const AssetDocumentsPanel = ({ open, onClose, asset }) => {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    if (!asset?.id) return;
    setLoadingDocs(true);
    try {
      const response = await assetDocumentsApi.getAll(asset.id);
      setDocuments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, [asset?.id]);

  useEffect(() => {
    if (open && asset?.id) {
      fetchDocuments();
    }
  }, [open, asset?.id, fetchDocuments]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description.trim()) {
        formData.append('description', description.trim());
      }
      await assetDocumentsApi.upload(asset.id, formData);
      setDescription('');
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await assetDocumentsApi.getDownloadUrl(asset.id, doc.id);
      const link = document.createElement('a');
      link.href = response.data.url;
      link.download = response.data.fileName || doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const handleView = async (doc) => {
    try {
      const response = await assetDocumentsApi.getDownloadUrl(asset.id, doc.id);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
    }
  };

  const handleDelete = async (doc) => {
    try {
      await assetDocumentsApi.delete(asset.id, doc.id);
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '75vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        pb: 1, borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Asset Documents
          </Typography>
          <Chip label={documents.length} size="small" color="primary" variant="outlined" />
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, minHeight: 300 }}>
        {/* Upload area */}
        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Button
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.doc,.docx,.zip"
            onChange={handleUpload}
          />
        </Box>

        {/* Document list */}
        {loadingDocs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No documents uploaded yet.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {documents.map((doc, index) => (
              <React.Fragment key={doc.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 1.5, px: 2.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {FILE_TYPE_ICONS[doc.file_type] || FILE_TYPE_ICONS.other}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {doc.file_name}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(doc.file_size)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(doc.created_at)}
                        </Typography>
                        {doc.uploaded_by_name && (
                          <Typography variant="caption" color="text.secondary">
                            by {doc.uploaded_by_name}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => handleView(doc)} sx={{ mr: 0.25 }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(doc)} sx={{ mr: 0.25 }}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(doc)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
                {doc.description && (
                  <Box sx={{ px: 2.5, pb: 1, pl: 8 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {doc.description}
                    </Typography>
                  </Box>
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssetDocumentsPanel;
